import { Injectable, inject } from '@angular/core';
import { Nomenclature, TypeDiplome } from '@app/core/models/nomenclature.model';
import {
    Nomenclature as NomenclatureApi,
    NomenclatureService as NomenclatureServiceApi,
    Pagination,
    ParametrageService as ParametrageServiceApi,
    Predicat as PredicatApi
} from '@pcscol/ref-api-v1-angular';
import { NEVER, Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { Cacheable } from 'ts-cacheable';
import { environment } from '../../../environments/environment';
export enum OperateurGeneral {
    Et = 'AND',
    Ou = 'OR'
}

export enum Operateur {
    Equal = '=',
    NotEqual = '!=',
    LessThan = '<',
    GreaterThan = '>',
    LessOrEqual = '<=',
    GreaterOrEqual = '>=',
    Re = '~'
}

@Injectable({
    providedIn: 'root'
})
export class InternalNomenclatureService {
    private parametrageService = inject(ParametrageServiceApi);
    private nomenclatureService = inject(NomenclatureServiceApi);

    @Cacheable({ async: false })
    listParametrages(
        codeParametrage: string,
        operateurLogique: 'AND' | 'OR',
        predicats: { [key: string]: PredicatApi }
    ): Observable<Array<NomenclatureApi>> {
        return this.parametrageService.lireListeParametragesAvecCriteres(codeParametrage, operateurLogique, predicats);
    }

    @Cacheable({ async: false })
    listNomenclatures(
        codeNomenclature: string,
        operateurLogique: 'AND' | 'OR',
        predicats: { [key: string]: PredicatApi },
        size?: number,
        page: number = 0
    ): Observable<Array<NomenclatureApi>> {
        if (size != null) {
            return this.nomenclatureService
                .lireListeNomenclaturesAvecCriteresPage(codeNomenclature, page, size, operateurLogique, predicats)
                .pipe(map(p => p.nomenclatures ?? []));
        } else {
            return this.nomenclatureService.lireListeNomenclaturesAvecCriteres(codeNomenclature, operateurLogique, predicats);
        }
    }
    @Cacheable({ async: false })
    listNomenclaturesPaginee(
        codeNomenclature: string,
        operateurLogique: 'AND' | 'OR',
        predicats: { [key: string]: PredicatApi },
        size?: number,
        page: number = 0
    ): Observable<Pagination> {
        if (size != null) {
            return this.nomenclatureService.lireListeNomenclaturesAvecCriteresPage(
                codeNomenclature,
                page,
                size,
                operateurLogique,
                predicats
            );
        } else {
            return NEVER;
        }
    }
}

export class Predicats {
    private predicats: { [key: string]: PredicatApi };

    constructor(predicats?: Predicats) {
        this.predicats = predicats?.predicats != null ? { ...predicats.predicats } : {};
    }

    public visible(): Predicats {
        this.addEstVisible();
        return this;
    }

    public valide(): Predicats {
        this.addValideAujourdhui();
        return this;
    }

    public toApi(): { [key: string]: PredicatApi } {
        return this.predicats;
    }

    public add(clef: string, operateur: Operateur, valeur: string): Predicats {
        const predicat: PredicatApi = {
            operateur: operateur,
            valeur: valeur
        };

        this.predicats[clef] = predicat;
        return this;
    }

    private addValideAujourdhui(): void {
        const now = new Date().toISOString().slice(0, 10);
        this.add('dateDebutValidite', Operateur.LessOrEqual, now);
        this.add('dateFinValidite', Operateur.GreaterOrEqual, now);
    }

    private addEstVisible(): void {
        this.add('temoinVisible', Operateur.Equal, String(true));
    }
}

export interface NomenclatureOptions {
    size?: number;
}

@Injectable({
    providedIn: 'root'
})
export class NomenclatureService {
    private service = inject(InternalNomenclatureService);

    /**
     * Lire les valeurs d'une nomenclature.
     * <ul>
     * <li>Valide à la date du jour</li>
     * <li>Visible</li>
     * </ul>
     *
     * @param codeNomenclature Le code de la nomenclature
     * @return Une liste de valeur
     */
    public listParametrages<E extends Nomenclature>(
        codeParametrage: string,
        converter?: (api: NomenclatureApi) => E,
        options?: NomenclatureOptions,
        predicats?: Predicats
    ): Observable<Array<E>> {
        const c = converter ? converter : api => <E>new Nomenclature(Nomenclature.fromApi(api));
        return this.listParametragesApi(codeParametrage, options, predicats).pipe(
            map(list => list.map(it => c(it))),
            map(parametrages => parametrages.sort((a, b) => a.compareTo(b)))
        );
    }

    /**
     * Lire les valeurs d'un paramétrage au format API.
     *
     * Par défaut (si options non spécifié), on récupère uniquement les données
     * valides.
     * <ul>
     * <li>Valide à la date du jour</li>
     * <li>Visible</li>
     * </ul>
     *
     * @param codeParametrage Le code du type de paramétrage à lire
     * @return Une liste de valeur
     */
    private listParametragesApi(
        codeParametrage: string,
        options?: NomenclatureOptions,
        predicats?: Predicats
    ): Observable<Array<NomenclatureApi>> {
        const p = predicats ?? new Predicats().visible().valide();
        return this.service.listParametrages(codeParametrage, OperateurGeneral.Et, p.toApi());
    }

    public listNomenclatures<E extends Nomenclature>(
        codeNomenclature: string,
        converter?: (api: NomenclatureApi) => E,
        options?: NomenclatureOptions,
        predicats?: Predicats,
        operateurLogique: OperateurGeneral = OperateurGeneral.Et
    ): Observable<Array<E>> {
        const c = converter ? converter : api => <E>new Nomenclature(Nomenclature.fromApi(api));
        return this.listNomenclaturesApi(codeNomenclature, options, predicats, operateurLogique).pipe(
            map(list => list.map(it => c(it))),
            map(parametrages => parametrages.sort((a, b) => a.compareTo(b)))
        );
    }

    public listNomenclaturesPaginee<E extends Nomenclature>(
        codeNomenclature: string,
        converter?: (api: NomenclatureApi) => E,
        options?: NomenclatureOptions,
        predicats?: Predicats,
        operateurLogique: OperateurGeneral = OperateurGeneral.Et,
        page: number = 0
    ) {
        const c = converter ? converter : api => <E>new Nomenclature(Nomenclature.fromApi(api));
        return this.listNomenclaturesApiPaginee(codeNomenclature, options, predicats, operateurLogique, page).pipe(
            map(p => ({ ...p, nomenclatures: p.nomenclatures!.map(it => c(it)) }))
        );
    }

    /**
     * récupère une valeur unique de nomenclature
     */
    public lireValeurUniqueNomenclature(codeNomenclature: string, codeRecherche: string): Observable<Nomenclature> {
        return this.listNomenclatures(
            codeNomenclature,
            undefined,
            { size: 1 },
            new Predicats().add('code', Operateur.Equal, codeRecherche)
        ).pipe(map(e => e[0]));
    }

    /**
     * récupère une valeur unique de paramétrage
     */
    public lireValeurUniqueParametrage(codeNomenclature: string, codeRecherche: string) {
        return this.service
            .listParametrages(codeNomenclature, 'AND', { code: { operateur: Operateur.Equal, valeur: codeRecherche } })
            .pipe(map(e => e[0]));
    }

    /**
     * Lire les valeurs d'une nomenclature au format API.
     *
     * <ul>
     * <li>Valide à la date du jour</li>
     * <li>Visible</li>
     * </ul>
     *
     * @param codeNomenclature Le code de la nomenclature
     * @return Une liste de valeur
     */
    private listNomenclaturesApi(
        codeNomenclature: string,
        options?: NomenclatureOptions,
        predicats?: Predicats,
        operateurLogique: OperateurGeneral = OperateurGeneral.Et
    ): Observable<Array<NomenclatureApi>> {
        const p = predicats ?? new Predicats().visible().valide();
        return this.service.listNomenclatures(codeNomenclature, operateurLogique, p.toApi(), options?.size);
    }

    private listNomenclaturesApiPaginee(
        codeNomenclature: string,
        options?: NomenclatureOptions,
        predicats?: Predicats,
        operateurLogique: OperateurGeneral = OperateurGeneral.Et,
        page: number = 0
    ) {
        const p = predicats ?? new Predicats().visible().valide();
        return this.service.listNomenclaturesPaginee(codeNomenclature, operateurLogique, p.toApi(), options?.size, page);
    }

    public listTypesDiplome(options?: NomenclatureOptions) {
        return this.listNomenclatures(environment.nomenclature.typeDiplome, api => new TypeDiplome(TypeDiplome.fromApi(api)), options).pipe(
            shareReplay(1)
        );
    }
}
